import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useMemo } from 'react';
import NotAvailable from 'src/assets/img/not-available.png';
import Button from 'src/components/design-system/Button';
import useCount from 'src/hooks/useCount';
import useWindowSize from 'src/hooks/useWindowSize';
import { InvoiceDetailsResponse } from 'src/services/useInvoiceDetailsApi';
import { IReceipt as ReceiptDetailsResponse } from 'src/types/Receipt';
import { validSignedUrl } from 'src/utils/strings';
import Image from '../Image';
import PopUpContentWrapper from '../PopUpContentWrapper';
import PreviewPDF from '../PreviewPDF';
import PopUp from '../design-system/PopUp';
import AttachExistingDocumentModal from './AttachExistingDocumentModal';
import DocumentTracker from './DocumentTracker';
import UploadScansModal from './UploadScansModal';
import useScansActions from './useScansActions';

interface ScansSectionProps {
  isInvoice: boolean;
  name: string;
  document: ReceiptDetailsResponse | InvoiceDetailsResponse;
  isLoading: boolean;
  id: string;
  companyId: string;
  getDocument: () => Promise<any>;
  isPoOpen: boolean;
  isInvoiceProcessed: boolean;
}

const ScanSection: FC<ScansSectionProps> = ({
  isInvoice,
  document,
  isLoading,
  id,
  companyId,
  getDocument,
  isPoOpen,
  isInvoiceProcessed,
}) => {
  const [
    currentDocumentIndex,
    incrementCurrentDocumentIndex,
    decrementCurrentDocumentIndex,
    setCurrentDocumentCount,
  ] = useCount();

  const attachment = useMemo(
    () => document?.attachments?.[currentDocumentIndex],
    [currentDocumentIndex, document?.attachments]
  );

  const {
    deleteCurrentDocument,
    isDeleting,
    isDeleteDisabled,
    isUnAttachDisabled,
    isUnAttaching,
    unAttachCurrentDocument,
    openUploadScans,
    closeUploadScans,
    isUploadScansOpen,
    isUploadDisabled,
    isAttachExistingDocumentsOpen,
    openAttachExistingDocuments,
    closeAttachExistingDocuments,
    attachDocuments,
    isAttaching,
    deleteDisableMessage,
    unAttachDisableMessage,
    uploadMessage,
    attachMessage,
    isAttachDisabled,
  } = useScansActions(
    attachment,
    currentDocumentIndex,
    decrementCurrentDocumentIndex,
    isInvoice,
    id,
    document,
    getDocument,
    isPoOpen,
    isInvoiceProcessed
  );

  const { height, width } = useWindowSize();

  const isImage = useMemo(
    () => !attachment?.description?.toLowerCase().endsWith('.pdf'),
    [attachment?.description]
  );

  const url = useMemo(() => `${validSignedUrl(attachment?.signedUrl)}`, [attachment?.signedUrl]);

  const handleAttach = useCallback(
    async (attachIds: number[]) => {
      await attachDocuments({
        id: Number(id),
        attachIds,
      });
      getDocument();
    },
    [attachDocuments, getDocument, id]
  );

  useEffect(() => {
    if (!isEmpty(document?.attachments) && currentDocumentIndex === -1) {
      setCurrentDocumentCount(0);
    }
  }, [currentDocumentIndex, document?.attachments, setCurrentDocumentCount]);

  return (
    <>
      <AttachExistingDocumentModal
        open={isAttachExistingDocumentsOpen}
        onClose={closeAttachExistingDocuments}
        onAttach={handleAttach}
        companyId={companyId}
        isInvoices={isInvoice}
      />
      <UploadScansModal
        open={isUploadScansOpen}
        onClose={closeUploadScans}
        isInvoice={isInvoice}
        id={id}
        companyId={companyId}
        getDocument={getDocument}
      />
      <div className="p-2 scans-section-with-actions">
        <div className="flex flex-row items-center justify-center flex-wrap">
          <PopUp
            content={<PopUpContentWrapper type="Error" contents={uploadMessage} />}
            show={isUploadDisabled}
          >
            <Button disabled={isUploadDisabled} onClick={openUploadScans}>
              Upload
            </Button>
          </PopUp>
          <PopUp
            content={<PopUpContentWrapper type="Error" contents={attachMessage} />}
            show={isAttachDisabled}
          >
            <Button
              onClick={openAttachExistingDocuments}
              disabled={isAttachDisabled}
              loading={isAttaching}
            >
              Attach
            </Button>
          </PopUp>
          <PopUp content={<PopUpContentWrapper type="Error" contents={unAttachDisableMessage} />}>
            <Button
              onClick={unAttachCurrentDocument}
              loading={isUnAttaching}
              color="dark"
              disabled={isUnAttachDisabled}
            >
              Un-attach
            </Button>
          </PopUp>
          <PopUp content={<PopUpContentWrapper type="Error" contents={deleteDisableMessage} />}>
            <Button
              color="error"
              onClick={deleteCurrentDocument}
              loading={isDeleting}
              disabled={isDeleteDisabled}
            >
              Delete
            </Button>
          </PopUp>
        </div>
        {isLoading ? (
          <div className="w-full h-24 flex items-center justify-center">
            <span className="k-icon k-i-loading text-xl" />
          </div>
        ) : (
          <>
            {attachment && (
              <DocumentTracker
                current={currentDocumentIndex + 1}
                total={document?.attachments?.length}
                increment={incrementCurrentDocumentIndex}
                decrement={decrementCurrentDocumentIndex}
              />
            )}
            {attachment && isImage && (
              <Image
                src={url}
                alt="preview"
                controls
                filename={attachment?.originalFile}
                styles={{
                  width: `${width}px`,
                  maxHeight: `${height - 180}px`,
                }}
              />
            )}
            {!isImage && <PreviewPDF url={url} height={`${height - 180}px`} />}

            {!attachment && (
              <img src={NotAvailable} alt="Not Available" className="mx-auto mt-4 block" />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ScanSection;
